import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderOpen, Plus, Pencil, Trash2, Upload, Download, FileText, AlertTriangle, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate } from "../lib/api";
import { apriFile } from "../components/FileViewer";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

export default function DocumentiAmministrazione() {
  const qc = useQueryClient();
  const { selectedStructure } = useAuth();
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(null);
  const [newName, setNewName] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [meta, setMeta] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const { data: sezioni = [] } = useQuery({ queryKey: ["doc_sezioni"], queryFn: async () => (await api.get("/doc_sezioni")).data });
  const { data: allDocs = [] } = useQuery({ queryKey: ["documenti-admin", selectedStructure], queryFn: async () => (await api.get("/documenti", { params: selectedStructure && selectedStructure !== "all" ? { structure_id: selectedStructure } : {} })).data });

  useEffect(() => { if (!active && sezioni.length) setActive(sezioni[0].nome); }, [sezioni, active]);

  const addSezione = async () => { if (!newName.trim()) return; await api.post("/doc_sezioni", { nome: newName.trim() }); setNewName(""); qc.invalidateQueries({ queryKey: ["doc_sezioni"] }); toast.success("Sezione creata"); };
  const renameSezione = async (id) => { await api.put(`/doc_sezioni/${id}`, { nome: editing.nome }); setEditing(null); qc.invalidateQueries({ queryKey: ["doc_sezioni"] }); toast.success("Rinominata"); };
  const delSezione = async (s) => { if (!window.confirm(`Eliminare la sezione "${s.nome}"?`)) return; await api.delete(`/doc_sezioni/${s.id}`); if (active === s.nome) setActive(null); qc.invalidateQueries({ queryKey: ["doc_sezioni"] }); toast.success("Sezione eliminata"); };

  const docs = allDocs.filter((d) => d.owner_type === "struttura" && d.categoria === active);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Seleziona un file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_type", "struttura");
      fd.append("categoria", active);
      if (selectedStructure && selectedStructure !== "all") fd.append("structure_id", selectedStructure);
      Object.entries(meta).forEach(([k, v]) => v && fd.append(k, v));
      if (!meta.titolo) fd.append("titolo", file.name);
      await api.post("/documenti/upload", fd);
      toast.success("Documento caricato"); setUploadOpen(false); setMeta({}); qc.invalidateQueries({ queryKey: ["documenti-admin", selectedStructure] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento"); } finally { setUploading(false); }
  };
  const download = (d) => apriFile(`/documenti/${d.id}/download`, d.original_filename || d.titolo || "documento");
  const removeDoc = async (id) => { await api.delete(`/documenti/${id}`); qc.invalidateQueries({ queryKey: ["documenti-admin", selectedStructure] }); toast.success("Eliminato"); };
  const isExpiring = (sc) => sc && sc <= new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-head font-bold flex items-center gap-2"><FolderOpen size={22} /> Documenti Amministrazione</h1>
        <p className="text-sm text-muted-foreground">Licenze, ascensori, caldaie, disinfestazione e sezioni personalizzate</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {sezioni.map((s) => (
          <div key={s.id} className="flex items-center">
            {editing?.id === s.id ? (
              <div className="flex items-center gap-1">
                <Input value={editing.nome} onChange={(e) => setEditing({ ...editing, nome: e.target.value })} className="h-9 w-40" data-testid={`sezione-rename-input-${s.id}`} />
                <button onClick={() => renameSezione(s.id)} className="p-1.5 text-emerald-600" data-testid={`sezione-rename-save-${s.id}`}><Check size={16} /></button>
                <button onClick={() => setEditing(null)} className="p-1.5 text-muted-foreground"><X size={16} /></button>
              </div>
            ) : (
              <button onClick={() => setActive(s.nome)} data-testid={`sezione-${s.id}`}
                className={`group flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active === s.nome ? "bg-primary text-white" : "bg-secondary hover:bg-muted"}`}>
                {s.nome}
                <span className={`flex items-center gap-1 ${active === s.nome ? "text-white/80" : "text-muted-foreground opacity-0 group-hover:opacity-100"}`}>
                  <Pencil size={12} onClick={(e) => { e.stopPropagation(); setEditing({ id: s.id, nome: s.nome }); }} data-testid={`sezione-edit-${s.id}`} />
                  <Trash2 size={12} onClick={(e) => { e.stopPropagation(); delSezione(s); }} data-testid={`sezione-del-${s.id}`} />
                </span>
              </button>
            )}
          </div>
        ))}
        <div className="flex items-center gap-1 ml-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nuova sezione..." className="h-9 w-40" data-testid="sezione-new-input" />
          <Button size="sm" variant="outline" onClick={addSezione} data-testid="sezione-add"><Plus size={15} /></Button>
        </div>
      </div>

      <div className="gr-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head font-bold">{active || "—"} · {docs.length} documenti</h2>
          <Button size="sm" onClick={() => { setMeta({}); setUploadOpen(true); }} disabled={!active} data-testid="doc-admin-upload"><Upload size={15} className="mr-1" /> Carica documento</Button>
        </div>
        <div className="space-y-2">
          {docs.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessun documento in questa sezione.</p>}
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border bg-white" data-testid={`doc-admin-${d.id}`}>
              <FileText size={18} className="text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{d.titolo}</div>
                <div className="text-xs text-muted-foreground">caricato {fmtDate(d.created_at)}{d.data_scadenza && ` · scade ${fmtDate(d.data_scadenza)}`}</div>
              </div>
              {isExpiring(d.data_scadenza) && <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600"><AlertTriangle size={13} /> In scadenza</span>}
              <Button size="sm" variant="ghost" onClick={() => download(d)} data-testid={`doc-admin-download-${d.id}`}><Download size={15} /></Button>
              <Button size="sm" variant="ghost" onClick={() => removeDoc(d.id)} data-testid={`doc-admin-del-${d.id}`}><Trash2 size={15} className="text-destructive" /></Button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Carica documento — {active}</DialogTitle><DialogDescription className="text-xs">Aggiungi un documento a questa sezione</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold mb-1 block">File</Label><Input type="file" ref={fileRef} data-testid="doc-admin-file" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Titolo</Label><Input value={meta.titolo || ""} onChange={(e) => setMeta((p) => ({ ...p, titolo: e.target.value }))} data-testid="doc-admin-titolo" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold mb-1 block">Data documento</Label><Input type="date" value={meta.data_documento || ""} onChange={(e) => setMeta((p) => ({ ...p, data_documento: e.target.value }))} /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Data scadenza</Label><Input type="date" value={meta.data_scadenza || ""} onChange={(e) => setMeta((p) => ({ ...p, data_scadenza: e.target.value }))} data-testid="doc-admin-scadenza" /></div>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Note</Label><Input value={meta.note || ""} onChange={(e) => setMeta((p) => ({ ...p, note: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setUploadOpen(false)}>Annulla</Button><Button onClick={upload} disabled={uploading} data-testid="doc-admin-save">{uploading ? "Caricamento..." : "Carica"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
